/**
 * BatchPickerRow
 * --------------
 * Row-companion component rendered underneath any line item whose
 * product has `batch_required=true`.
 *
 * Extracted from CreateInvoice.jsx (Feb 2026 Wave-3) — the parent file
 * was 1200+ lines. This is a pure UI helper: the caller owns the line
 * data and receives allocation changes via `onChange`. No business
 * logic lives here, just the batch-allocation dialog + summary chips.
 *
 * Behaviour:
 *   - When the parent hasn't allocated batches yet, we surface the
 *     backend-suggested FEFO placement (or a shortfall warning).
 *   - Clicking "Change batch" opens a dialog to manually distribute
 *     the line quantity across `batch_options` (which the parent
 *     hydrates from `/api/batches?product_id=…`).
 *   - Applying only commits when the sum of allocations matches the
 *     requested line qty (soft equality to 1e-4).
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../ui/dialog';
import { Boxes } from 'lucide-react';

const BatchPickerRow = ({ li, onChange }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState([]); // [{batch_id, quantity, batch_number, expiry_date}]
    const allocs = li.batch_allocations || [];
    const shortfall = Number(li.batch_shortfall || 0);
    const options = li.batch_options || [];

    useEffect(() => {
        if (open) setDraft(allocs.map((a) => ({ ...a })));
    }, [open]);

    const draftTotal = draft.reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0);
    const lineQty = parseFloat(li.quantity) || 0;
    const valid = Math.abs(draftTotal - lineQty) < 0.0001;

    const setDraftAlloc = (batch_id, qty) => {
        const opt = options.find((o) => o.id === batch_id);
        const existing = draft.find((a) => a.batch_id === batch_id);
        const next = draft.filter((a) => a.batch_id !== batch_id);
        const num = parseFloat(qty);
        if (num > 0) {
            next.push({
                batch_id, quantity: +num.toFixed(4),
                batch_number: opt?.batch_number || existing?.batch_number,
                expiry_date: opt?.expiry_date || existing?.expiry_date,
            });
        }
        setDraft(next);
    };

    const apply = () => {
        onChange({ batch_allocations: draft.map((a) => ({ ...a })), batch_shortfall: 0 });
        setOpen(false);
    };

    return (
        <div className="flex items-start justify-between gap-3 flex-wrap" data-testid={`batch-picker-${li.product_id}`}>
            <div className="flex items-center gap-3 flex-wrap text-xs">
                <Boxes size={14} className="text-[#C08478]" />
                <span className="uppercase tracking-wider text-[10px] text-[#C08478] font-semibold">FEFO</span>
                {shortfall > 0 ? (
                    <span className="text-[#E76F51] font-medium" data-testid="batch-shortfall">
                        Insufficient batch stock — shortfall {shortfall} unit{shortfall > 1 ? 's' : ''}. Receive a new batch.
                    </span>
                ) : allocs.length === 0 ? (
                    <span className="text-gray-400">No allocation yet (enter quantity).</span>
                ) : (
                    <div className="flex gap-2 flex-wrap">
                        {allocs.map((a) => (
                            <span key={a.batch_id} className="bg-white border border-[#E5E5E5] rounded px-2 py-0.5 font-mono">
                                <span className="text-[#1A1A1A] font-semibold">{a.batch_number}</span>
                                <span className="text-gray-400 mx-1">·</span>
                                <span>{a.quantity}</span>
                                {a.expiry_date && (<span className="text-gray-400 ml-1">exp {a.expiry_date}</span>)}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        data-testid={`change-batch-btn-${li.product_id}`}
                        className="text-[#C08478] hover:text-[#1A1A1A] h-7 text-xs"
                        disabled={!li.product_id}
                    >
                        Change batch
                    </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-picker-dialog">
                    <DialogHeader>
                        <DialogTitle>Allocate Batches — {li.product_name}</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-gray-500 mb-3">
                        Distribute the line quantity (<span className="font-mono font-semibold">{lineQty}</span>) across active batches.
                        FEFO suggestions appear first by oldest expiry.
                    </div>
                    {options.length === 0 ? (
                        <div className="py-6 text-center text-gray-400 text-sm">No active batches available for this product.</div>
                    ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                            {options.map((b) => {
                                const allocated = draft.find((a) => a.batch_id === b.id)?.quantity ?? '';
                                return (
                                    <div key={b.id} className="flex items-center gap-3 p-2 rounded border border-[#E5E5E5]">
                                        <div className="flex-1">
                                            <p className="text-sm font-mono font-semibold">{b.batch_number}</p>
                                            <p className="text-[10px] text-gray-500">
                                                exp {b.expiry_date} · remaining {b.quantity_remaining} · {b.warehouse}
                                            </p>
                                        </div>
                                        <Input
                                            type="number" min="0" step="0.01" max={b.quantity_remaining}
                                            value={allocated}
                                            onChange={(e) => setDraftAlloc(b.id, e.target.value)}
                                            data-testid={`alloc-input-${b.id}`}
                                            className="w-28 text-right font-mono"
                                            placeholder="0"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="flex justify-between text-sm mt-3">
                        <span>Allocated</span>
                        <span className={`font-mono font-semibold ${valid ? 'text-[#2A9D8F]' : 'text-[#E76F51]'}`} data-testid="alloc-total">
                            {draftTotal} / {lineQty}
                        </span>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button type="button" disabled={!valid} onClick={apply} data-testid="apply-batch-alloc"
                                className="bg-[#1A1A1A] hover:bg-[#C08478] text-white">
                            Apply
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BatchPickerRow;
